import { useState } from "react";
import { Formulario } from "@/types/formulario";
import { useFormularioPublicSettings } from "@/hooks/useFormularioPublicSettings";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Copy, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";

interface PublicFormSettingsProps {
  formulario: Formulario;
}

export function PublicFormSettings({ formulario }: PublicFormSettingsProps) {
  const { updatePublicSettings, regenerateToken, isUpdating, isRegenerating } = 
    useFormularioPublicSettings(formulario.id);

  const [publico, setPublico] = useState(formulario.publico || false);
  const [permiteAnonimo, setPermiteAnonimo] = useState(formulario.permite_anonimo || false);
  const [permiteMultiplasRespostas, setPermiteMultiplasRespostas] = useState(
    formulario.permite_multiplas_respostas || false
  );
  const [dataExpiracao, setDataExpiracao] = useState(
    formulario.data_expiracao ? new Date(formulario.data_expiracao).toISOString().split('T')[0] : ''
  );
  const [limiteRespostas, setLimiteRespostas] = useState<number | ''>(
    formulario.limite_respostas || ''
  );

  const publicLink = formulario.token_publico 
    ? `${window.location.origin}/f/${formulario.token_publico}`
    : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicLink);
    toast.success("Link copiado para área de transferência!");
  };

  const handleOpenLink = () => {
    window.open(publicLink, '_blank');
  };

  const handleSave = () => {
    updatePublicSettings({
      publico,
      permite_anonimo: permiteAnonimo,
      permite_multiplas_respostas: permiteMultiplasRespostas,
      data_expiracao: dataExpiracao || null,
      limite_respostas: limiteRespostas || null,
    });
  };

  const handleRegenerate = () => {
    if (confirm('Tem certeza que deseja regenerar o link? O link antigo deixará de funcionar.')) {
      regenerateToken();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configurações de Acesso Público</CardTitle>
        <CardDescription>
          Configure como este formulário pode ser acessado publicamente
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Toggle Público */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Acesso Público</Label>
            <p className="text-sm text-muted-foreground">
              Permitir que qualquer pessoa acesse o formulário via link
            </p>
          </div>
          <Switch checked={publico} onCheckedChange={setPublico} />
        </div>

        {publico && (
          <>
            {/* Link Público */}
            {publicLink && (
              <div className="space-y-2">
                <Label>Link Público</Label>
                <div className="flex gap-2">
                  <Input value={publicLink} readOnly className="flex-1" />
                  <Button variant="outline" size="icon" onClick={handleCopyLink}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={handleOpenLink}>
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={handleRegenerate}
                    disabled={isRegenerating}
                  >
                    <RefreshCw className={`h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Total de respostas: {formulario.total_respostas || 0}
                </p>
              </div>
            )}

            {/* Permitir Anônimo */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Respostas Anônimas</Label>
                <p className="text-sm text-muted-foreground">
                  Permitir que usuários respondam sem fazer login
                </p>
              </div>
              <Switch checked={permiteAnonimo} onCheckedChange={setPermiteAnonimo} />
            </div>

            {/* Múltiplas Respostas */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Múltiplas Respostas</Label>
                <p className="text-sm text-muted-foreground">
                  Permitir que a mesma pessoa responda mais de uma vez
                </p>
              </div>
              <Switch 
                checked={permiteMultiplasRespostas} 
                onCheckedChange={setPermiteMultiplasRespostas} 
              />
            </div>

            {/* Data de Expiração */}
            <div className="space-y-2">
              <Label>Data de Expiração (opcional)</Label>
              <Input
                type="date"
                value={dataExpiracao}
                onChange={(e) => setDataExpiracao(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-muted-foreground">
                Após esta data, o formulário não aceitará mais respostas
              </p>
            </div>

            {/* Limite de Respostas */}
            <div className="space-y-2">
              <Label>Limite de Respostas (opcional)</Label>
              <Input
                type="number"
                min="1"
                placeholder="Sem limite"
                value={limiteRespostas}
                onChange={(e) => setLimiteRespostas(e.target.value ? parseInt(e.target.value) : '')}
              />
              <p className="text-xs text-muted-foreground">
                Número máximo de respostas permitidas
              </p>
            </div>

            {/* Botão Salvar */}
            <Button onClick={handleSave} disabled={isUpdating} className="w-full">
              {isUpdating ? 'Salvando...' : 'Salvar Configurações'}
            </Button>
          </>
        )}

        {!publico && (
          <Button onClick={handleSave} disabled={isUpdating} className="w-full">
            {isUpdating ? 'Salvando...' : 'Desativar Acesso Público'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
